import {useEffect, useState} from "react";
import {Head} from "@inertiajs/react";
import Layout from "@spa/components/layouts/pc/Layout";
import JobDetail from "@spa/features/search_job/pc/components/JobDetail";
import JobList from "@spa/features/search_job/pc/components/JobList";
import {SearchJobNoResult, SearchJobNoResultPaging} from "@spa/features/search_job/pc/components/SearchJobNoResult";
import Header from "@spa/components/layouts/pc/Header";
import Footer from "@spa/components/layouts/pc/Footer";
import KnowledgePanel from "@spa/features/search_job/pc/components/knowledge_panel/KnowledgePanel";
import {initHeaderModal} from "@design/pc/modules/headerModal";
import {initHelpModal} from "@design/pc/modules/helpModal";
import {initAlert} from "@design/pc/modules/alert";
import {saveSearchHistory} from "@src/common/service/searchHistory";
import {fetchSavedJobs, normalizeLocation} from "@src/common/service/savedJobs";
import {saveToLocalStorage} from "@spa/utils/localStorageHelper";
import {SavedJob} from "@src/common/model/SavedJob";
import {BrowserRouter as Router} from "react-router-dom";
import {JobSwitchProvider} from "@spa/features/search_job/pc/contexts/JobSwitchContext";
import {initFilter} from "@design/pc/modules/filter";
import {SearchProvider} from "@spa/features/search_job/pc/contexts/SearchContext";
import {FocusProvider} from "@spa/features/search_job/pc/contexts/FocusContext";
import {JobAlertProvider} from "@spa/features/search_job/pc/contexts/JobAlertContext";
import {trackSearchResult} from "@spa/features/search_job/pc/api/analytics/trackPage";
import {TrackProvider} from "@spa/features/search_job/pc/contexts/TrackContext";
import {trackSaveJobButtonOn, trackSaveJobButtonOff, trackSearchBoxTotalClick} from "@spa/features/search_job/pc/api/analytics/trackEvent";
import NotificationOverlay from "@spa/features/search_job/pc/components/NotificationOverlay";

const SearchJob = (props) => {
    /**
     * お気に入り件数の初期化
     */
    const initializeSavedJobsCount = () => {
        const savedJobs = fetchSavedJobs(false);
        return savedJobs.length;
    };

    /**
     * お気に入り件数の状態管理
     */
    const [savedJobsCount, setSavedJobsCount] = useState(initializeSavedJobsCount);

    /**
     * お気に入り件数を更新する関数
     */
    const updateSavedJobsCount = () => {
        const savedJobs = fetchSavedJobs(false);
        setSavedJobsCount(savedJobs.length);
    };

    /**
     * 検索結果を表示するかどうか
     */
    const isShowSearchResult = props.hasResult && props.searchResultViewModel.jobViewModelList.length > 0;
    /**
     * ページングの結果0件になったかどうか
     */
    const isPagingNoResult = props.hasResult && props.searchResultViewModel.jobViewModelList.length === 0;

    /**
     * 検索結果の計測が完了しているか
     */
    const [isSearchResultTracked, setIsSearchResultTracked] = useState(false);
    if (!isSearchResultTracked) {
        // 検索結果ページ表示の計測
        trackSearchResult(props.searchResultViewModel);
        setIsSearchResultTracked(true);
    }

    useEffect(() => {
        // design entry
        initHeaderModal();
        initHelpModal();
        initFilter();
        initAlert();
        // 検索履歴の保存
        saveSearchHistory();

        // 求人パラメータ付与のscriptタグ生成
        addScriptToContainer();
    }, []);

    /**
     * お気に入りの初期化
     */
    const initializeSavedJobs = () => {
        const savedJobsForSearchResult = {};
        const savedJobs = fetchSavedJobs();
        props.searchResultViewModel.jobViewModelList.forEach((jobViewModel) => {
            // Check if the job in the search results is saved
            const isSaved = savedJobs.some((savedJob) => savedJob.uniqueId === jobViewModel.uniqueId);
            savedJobsForSearchResult[jobViewModel.uniqueId] = isSaved;
        });
        return savedJobsForSearchResult;
    };

    /**
     * 検索結果の求人がお気に入りに登録されているか
     * {uniqueId: isSaved, ...}
     */
    const [savedJobsForSearchResult, setSavedJobsForSearchResult] = useState(initializeSavedJobs);

    /**
     * 計測用の設置値
     */
    const channelName = props.searchResultViewModel.analyticsViewModel.channelName;

    /**
     * お気に入りの保存・削除
     * @param e
     * @param {object} job
     */
    const handleSaveJob = (e, job) => {
        e.stopPropagation();
        // お気に入りリスト取得
        const savedJobs = fetchSavedJobs();
        const jobUniqueId = job.uniqueId;
        const isSaved = savedJobsForSearchResult[jobUniqueId]

        if (isSaved) {
            // お気に入りから対象jobを削除
            const updatedSavedJobs = savedJobs.filter((jobData) => jobData['uniqueId'] !== jobUniqueId);
            saveToLocalStorage('saved_jobs', updatedSavedJobs);

            // state を更新
            setSavedJobsForSearchResult({
                ...savedJobsForSearchResult,
                [jobUniqueId]: false
            });

            // お気に入り削除を計測
            trackSaveJobButtonOff(channelName, jobUniqueId);
        } else {
            // 正規化対象の勤務地検索の時、勤務地表記を正規化
            if (props.searchResultViewModel.shouldNormalizeLocation) {
                job['location'] = normalizeLocation(job['location']);
            }

            const savedJob = new SavedJob(job);

            // お気に入りが30件を超えないように削除
            while (savedJobs.length >= 30) {
                savedJobs.pop();
            }

            // お気に入りリストにjobデータを追加
            savedJobs.unshift(savedJob);
            saveToLocalStorage('saved_jobs', savedJobs);

            // state を更新
            setSavedJobsForSearchResult({
                ...savedJobsForSearchResult,
                [jobUniqueId]: true
            });

            // お気に入り登録を計測
            trackSaveJobButtonOn(channelName, jobUniqueId);
        }

        // ヘッダーバッジを更新
        updateSavedJobsCount();
    }

    /**
     * 求人パラメータ付与のscript生成
     */
    const addScriptToContainer = () => {
        // 検索結果がない場合はscriptを生成しない
        if (!isShowSearchResult) {
            return;
        }
        const scriptContainer = document.getElementById("script-container");
        const script = document.createElement("script");
        script.src = "webfront/js/direct/addUrlParameter.js";
        script.async = true;
        scriptContainer.appendChild(script);
    }

    return (
        <Router>
            <TrackProvider searchResultAnalyticsViewModel={props.searchResultViewModel.analyticsViewModel} >
                <SearchProvider
                    initialKeyword={props.searchResultViewModel.keyword}
                    initialLocation={props.searchResultViewModel.locationWord}
                    isFromPaidJob={props.isFromPaidJob}
                    isFromCrawler={props.isFromCrawler}
                    searchedKeyword={props.searchResultViewModel.keyword}
                    searchedLocation={props.searchResultViewModel.locationWord}
                    narrowSearchParamsViewModel={props.narrowSearchParamsViewModel}
                    distanceDefaultValue={props.distanceDefaultValue}
                    keywordPlaceholder={props.keywordPlaceholder}
                    locationPlaceholder={props.locationPlaceholder}
                    onExecuteAnalytics={() => trackSearchBoxTotalClick(channelName)}
                >
                    <Layout>
                        <Head>
                            <title>{props.searchResultViewModel.title}</title>
                            <meta name="robots" content={props.searchResultViewModel.metaTextViewModel.robots}/>
                            <meta name="description" content={props.searchResultViewModel.metaTextViewModel.description}/>
                            {props.searchResultViewModel.metaTextViewModel.isIndex && (
                                <link rel="canonical" href={props.searchResultViewModel.canonicalUrl}/>
                            )}
                        </Head>
                        <Header
                            hasSearchWindow={true}
                            h1={props.searchResultViewModel.header}
                            keyword={props.searchResultViewModel.keyword}
                            location={props.searchResultViewModel.locationWord}
                            narrowSearchParamsViewModel={props.narrowSearchParamsViewModel}
                            searchResultViewModel={props.searchResultViewModel}
                            modalViewModel={props.modalViewModel}
                            savedJobsCount={savedJobsCount}
                        />
                        <JobAlertProvider
                            searchCondition={props.searchResultViewModel.jobAlertViewModel.jobAlertConditionParameters}
                            conditionString={props.searchResultViewModel.jobAlertViewModel.jobAlertConditionString}
                            isFromPaidJob={props.isFromPaidJob}
                            isDoubleOptIn={props.isDoubleOptIn}
                        >
                            {isShowSearchResult &&
                                <main className="s-main">
                                    <div className="l-c-result l-c-result--search s-help_lock_wrap">
                                        <JobSwitchProvider searchResultViewModel={props.searchResultViewModel} validatedUniqueId={props.validatedUniqueId}>
                                            <FocusProvider>
                                                <JobList
                                                    searchResultViewModel={props.searchResultViewModel}
                                                    savedJobsForSearchResult={savedJobsForSearchResult}
                                                    handleSaveJob={handleSaveJob}
                                                />
                                                <JobDetail
                                                    shouldNormalizeLocation={props.searchResultViewModel.shouldNormalizeLocation}
                                                    savedJobsForSearchResult={savedJobsForSearchResult}
                                                    handleSaveJob={handleSaveJob}
                                                    translationsJobItems={props.searchResultViewModel.translations.job_items}
                                                />
                                            </FocusProvider>
                                        </JobSwitchProvider>
                                    </div>
                                    <KnowledgePanel searchResultViewModel={props.searchResultViewModel} />
                                    <div id="script-container">
                                        <div id="data-container"
                                            data-first-unique-id={props.searchResultViewModel.jobViewModelList[0].uniqueId}
                                            data-validated-unique-id={props.validatedUniqueId}
                                        ></div>
                                    </div>
                                </main>
                            }
                            {isPagingNoResult &&
                                <SearchJobNoResultPaging
                                    searchResultViewModel={props.searchResultViewModel}
                                />
                            }
                            {!isShowSearchResult && !isPagingNoResult &&
                                <SearchJobNoResult
                                    translations={props.searchResultViewModel.translations}
                                    isDisplayJobAlert={props.searchResultViewModel.isDisplayJobAlert}
                                />
                            }
                        </JobAlertProvider>
                        <Footer/>
                        <NotificationOverlay 
                            translations={props.searchResultViewModel.translations}
                        />
                    </Layout>
                </SearchProvider>
            </TrackProvider>
        </Router>
    );
}

export default SearchJob;
